import type { HeatBand, HeatScore, HeatSignal } from './types';

/**
 * Scoring — converts buffered signals into per-subject heat.
 * MOCK: simple weighted sum with time decay.
 * Real implementation lives server-side; this exists so the UI can render
 * coherent bands during prototype.
 */

const HALF_LIFE_MS = 1000 * 60 * 60 * 6;

const decay = (at: number, ref: number): number => {
  const age = Math.max(0, ref - at);
  return Math.pow(0.5, age / HALF_LIFE_MS);
};

const bandFor = (value: number): HeatBand => {
  if (value >= 12) return 'pulse';
  if (value >= 6) return 'hot';
  if (value >= 2) return 'warm';
  return 'cool';
};

export const scoreSubjects = (
  signals: readonly HeatSignal[],
  refTime: number = Date.now(),
): Map<string, HeatScore> => {
  const acc = new Map<string, number>();

  for (const s of signals) {
    if (!s.subjectId) continue;
    const prev = acc.get(s.subjectId) ?? 0;
    acc.set(s.subjectId, prev + s.weight * decay(s.at, refTime));
  }

  const result = new Map<string, HeatScore>();
  acc.forEach((value, subjectId) => {
    result.set(subjectId, {
      value,
      band: bandFor(value),
      updatedAt: refTime,
    });
  });

  return result;
};
